import React, { useState, ReactElement, useEffect } from 'react';
import { createMailtoLink } from '../../../utils/url-utility';

const MFA_METHOD_SMS = 'sms';
const MFA_METHOD_TOTP = 'totp';

interface EnrollScreenProps {
  userPhone: string;
  setUserPhone: React.Dispatch<React.SetStateAction<string>>;
  handleSendCodeClick: () => void;
  showErrorMsg: boolean;
  isMfaForcedByOrgAdmin: boolean;
  onSwitchToTotp: () => void;
}
const EnrollScreen = ({
  userPhone,
  setUserPhone,
  handleSendCodeClick,
  showErrorMsg,
  isMfaForcedByOrgAdmin,
  onSwitchToTotp,
}: EnrollScreenProps) => (
  <>
    <h2
      cy-data="2fa-screen-header"
      className="text-left text-3xl font-semibold text-gray-900">
      Secure Your Account
    </h2>
    <div className="flex flex-col font-medium">
      <span className="text-sm text-gray-600">Protect and authenticate your account by entering a 6-digit code sent to your mobile device.</span>
      <span className="text-xs mt-1 text-gray-500">*Currently only supported for US phone numbers</span>
    </div>
    <div className="flex flex-col">
      <label className="block text-sm font-medium text-gray-700">Phone Number</label>
      <div className="flex mt-1 appearance-none w-full border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm">
        <span className="flex items-center justify-center w-12">+1</span>
        <input
          cy-data="phone-number-field"
          type="phone"
          autoFocus={true}
          value={userPhone}
          onChange={(e) => setUserPhone(e.target.value)}
          defaultValue={userPhone}
          className="block px-3 py-2 w-full rounded-r-md"
        />
      </div>
    </div>
    {showErrorMsg && <div className="block text-sm font-medium text-red-500">Please enter a valid phone number.</div>}
    <div>
      <button
        cy-data="send-code-button"
        type="button"
        className="my-auto bg-gradient-to-t from-blue-600 to-blue-500 cursor-pointer h-auto w-full md:w-1/2 flex justify-center py-2 px-4 border border-blue-600 rounded-md drop-shadow-xl text-md font-medium text-white focus:outline-none hover:bg-blue-500"
        onClick={handleSendCodeClick}
      >
        Send SMS Code
      </button>
    </div>
    <div className="block text-sm font-medium text-gray-600">
      Message and data rates may apply
    </div>
    <button
      type="button"
      onClick={onSwitchToTotp}
      className="text-sm font-medium text-blue-600 hover:text-blue-700 text-left"
    >
      Use an authenticator app instead (Apple Passwords, Google Authenticator, etc.)
    </button>
    {!isMfaForcedByOrgAdmin && (
      <button type="submit" className="text-sm">
        <span className="font-medium text-blue-600 hover:text-blue-600-600 pb-8">Skip this step</span>
      </button>
    )}
  </>
);

interface TotpEnrollScreenProps {
  qrCodeDataUrl: string | null;
  totpSecret: string | null;
  onSwitchToSms: () => void;
  onQrScanned: () => void;
  isMfaForcedByOrgAdmin: boolean;
}
const TotpEnrollScreen = ({
  qrCodeDataUrl,
  totpSecret,
  onSwitchToSms,
  onQrScanned,
  isMfaForcedByOrgAdmin,
}: TotpEnrollScreenProps) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (!totpSecret) return;
    void window.navigator.clipboard.writeText(totpSecret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
  <>
    <h2 cy-data="2fa-screen-header" className="text-left text-3xl font-semibold text-gray-900">
      Set Up Authenticator App
    </h2>
    <p className="text-sm text-gray-600">
      Scan this QR code with Apple Passwords, Google Authenticator, or any TOTP app, then click Continue to enter your first code.
    </p>
    {qrCodeDataUrl ? (
      <img src={qrCodeDataUrl} alt="TOTP QR code" className="w-48 h-48" />
    ) : (
      <div className="w-48 h-48 flex items-center justify-center bg-gray-100 rounded">
        <span className="text-sm text-gray-500">Loading…</span>
      </div>
    )}
    {totpSecret && (
      <div className="flex flex-col space-y-1">
        <p className="text-xs text-gray-500">Can&apos;t scan? Enter this code manually in your app:</p>
        <div className="flex items-center space-x-2">
          <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded tracking-widest">{totpSecret}</code>
          <button
            type="button"
            onClick={handleCopy}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>
    )}
    <button
      type="button"
      onClick={onQrScanned}
      disabled={!qrCodeDataUrl}
      className="bg-gradient-to-t from-blue-600 to-blue-500 cursor-pointer h-auto w-full md:w-1/2 flex justify-center py-2 px-4 border border-blue-600 rounded-md drop-shadow-xl text-md font-medium text-white focus:outline-none hover:bg-blue-500 disabled:opacity-50"
    >
      I&apos;ve scanned it — Continue
    </button>
    <button
      type="button"
      onClick={onSwitchToSms}
      className="text-sm font-medium text-blue-600 hover:text-blue-700 text-left"
    >
      Use SMS instead
    </button>
    {!isMfaForcedByOrgAdmin && (
      <button type="submit" className="text-sm">
        <span className="font-medium text-blue-600 hover:text-blue-600-600 pb-8">Skip this step</span>
      </button>
    )}
  </>
  );
};

interface ValidationScreenProps {
  setSentCode: React.Dispatch<React.SetStateAction<boolean>>;
  displayUserPhone: () => string;
  firstName: string;
  isTwoFactorRequired: boolean;
  handleSendCodeClick: () => void;
  mfaMethod: string;
}
const ValidationScreen = ({
  displayUserPhone,
  setSentCode,
  firstName,
  isTwoFactorRequired,
  handleSendCodeClick,
  mfaMethod,
}: ValidationScreenProps) => {
  const [userCode, setUserCode] = useState<string>('');
  const [cooldownTime, setCooldownTime] = useState<number>(30);
  const [showResendSuccess, setShowResendSuccess] = useState<boolean>(false);
  const isTotp = mfaMethod === MFA_METHOD_TOTP;
  const supportEmailLink = createMailtoLink({
    toAddress: 'hey@halda.ai',
    subject: '2FA recovery support',
    body: 'Hey Halda, I am having trouble accessing my account with 2FA. Could I get some help recovering my account?',
  });

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (cooldownTime > 0) {
      timer = setInterval(() => {
        setCooldownTime((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldownTime]);

  const handleResendCode = () => {
    handleSendCodeClick();
    setCooldownTime(30);
    setShowResendSuccess(true);
    setTimeout(() => setShowResendSuccess(false), 3000);
  };

  return (
    <>
      <h2 className="text-left text-3xl font-bold text-gray-900">{firstName ? `Welcome Back, ${firstName}` : 'Welcome Back'}</h2>
      <div className="block text-sm font-medium text-gray-600">
        {isTotp
          ? 'Enter the 6-digit code from your authenticator app.'
          : 'Thank you for keeping your account secure. Check your mobile device for a code:'}
      </div>
      {!isTotp && (
        <div className="flex items-center space-x-3">
          <span className="block text-sm font-medium text-gray-900">{displayUserPhone()}</span>
          {!isTwoFactorRequired && (
            <button
              type="button"
              onClick={() => setSentCode(false)}
              className="font-medium text-sm text-blue-600 hover:text-blue-600-600"
            >
              Edit?
            </button>
          )}
        </div>
      )}
      <div className="flex flex-col">
        <div className="flex justify-between">
          <label className="block text-sm font-medium text-gray-700">Code</label>
          {!isTotp && (
            <button
              type="button"
              onClick={handleResendCode}
              disabled={cooldownTime > 0}
              className={`font-medium text-sm ${cooldownTime > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-600-600'}`}
            >
              {cooldownTime > 0 ? `Resend in ${cooldownTime}s` : 'Resend Code'}
            </button>
          )}
        </div>
        {showResendSuccess && (
          <div className="mt-1 text-sm text-green-600">
            Verification code has been resent successfully
          </div>
        )}
        <div className="mt-1">
          <input
            cy-data="otp-field"
            type="text"
            name="user[otp_attempt]"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus={true}
            autoComplete="one-time-code"
            onChange={(e) => setUserCode(e.target.value)}
            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none sm:text-sm" />
        </div>
      </div>
      <div>
        <button
          cy-data="otp-submit-button"
          type="submit"
          disabled={userCode.length === 0}
          className="bg-gradient-to-t from-blue-600 to-blue-500 cursor-pointer h-auto w-full md:w-1/2 flex justify-center py-2 px-4 border border-blue-600 rounded-md drop-shadow-xl text-md font-medium text-white focus:outline-none hover:bg-blue-500 whitespace-nowrap"
        >
          Securely Sign In
        </button>
      </div>
      {!isTotp && (
        <div className="block text-sm font-medium text-gray-500">
          <span>Didn&apos;t receive a code?</span>
          <div className="flex flex-row space-x-1">
            <span>Contact</span>
            <a
              rel="noreferrer"
              href={supportEmailLink}
              target="_blank"
              title="Contact Halda Support"
              aria-label="Contact Halda Support"
            >
              <span className="text-blue-600">hey@halda.ai</span>
            </a>
          </div>
        </div>
      )}
    </>
  );
};

interface AuthenticateUserScreenProps {
  sentCode: boolean;
  setSentCode: React.Dispatch<React.SetStateAction<boolean>>;
  userEmail: string;
  userPassword: string;
  userPhone: string;
  setUserPhone: React.Dispatch<React.SetStateAction<string>>;
  isRememberable: boolean;
  firstName: string;
  isTwoFactorRequired: boolean;
  isMfaForcedByOrgAdmin: boolean;
  mfaMethod: string;
  setMfaMethod: React.Dispatch<React.SetStateAction<string>>;
}

const AuthenticateUserScreen = ({
  sentCode,
  setSentCode,
  userEmail,
  userPassword,
  userPhone,
  setUserPhone,
  isRememberable,
  firstName,
  isTwoFactorRequired,
  isMfaForcedByOrgAdmin,
  mfaMethod,
  setMfaMethod,
}: AuthenticateUserScreenProps): ReactElement => {
  const [showErrorMsg, setShowErrorMsg] = useState<boolean>(false);
  const [enrollMode, setEnrollMode] = useState<string>(mfaMethod);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);

  const handleSendCodeClick = () => {
    if (userPhone.length < 10) {
      setShowErrorMsg(true);
    } else {
      fetch('/authenticate/enroll-in-mfa', {
        method: 'POST',
        body: JSON.stringify({ username: userEmail, password: userPassword, phone_number: userPhone }),
        headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      })
        .then((resp) => resp.json().then((body) => ({ ok: resp.ok, body })))
        .then(({ ok, body }) => {
          if (ok && body.success) {
            setShowErrorMsg(false);
            setSentCode(true);
          } else {
            setShowErrorMsg(true);
          }
        })
        .catch(() => setShowErrorMsg(true));
    }
  };

  const handleSwitchToTotp = () => {
    setEnrollMode(MFA_METHOD_TOTP);
    fetch('/authenticate/totp-setup', {
      method: 'POST',
      body: JSON.stringify({ username: userEmail, password: userPassword }),
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    })
      .then((resp) => resp.json().then((body) => ({ ok: resp.ok, body })))
      .then(({ ok, body }) => {
        if (ok && body.success) {
          setQrCodeDataUrl(body.qr_code_data_url);
          setTotpSecret(body.totp_secret);
        } else {
          setShowErrorMsg(true);
        }
      })
      .catch(() => setShowErrorMsg(true));
  };

  const handleQrScanned = () => {
    setMfaMethod(MFA_METHOD_TOTP);
    setSentCode(true);
  };

  const displayUserPhone = () => {
    const lastFourDigitsOfPhone = userPhone.slice(-4);
    return `(xxx)-xxx-${lastFourDigitsOfPhone}`;
  };

  return (
    <div className="space-y-6">
      <input type="hidden" name="user[email]" value={userEmail} />
      <input type="hidden" name="user[password]" value={userPassword} />
      <input type="hidden" name="user[remember_me]" value={isRememberable ? '1' : ''} />
      <input type="hidden" name="user[phone_number]" value={userPhone} />
      <input type="hidden" name="user[mfa_method]" value={mfaMethod} />
      {!sentCode && enrollMode !== MFA_METHOD_TOTP && (
        <EnrollScreen
          userPhone={userPhone}
          setUserPhone={setUserPhone}
          handleSendCodeClick={handleSendCodeClick}
          showErrorMsg={showErrorMsg}
          isMfaForcedByOrgAdmin={isMfaForcedByOrgAdmin}
          onSwitchToTotp={handleSwitchToTotp}
        />
      )}
      {!sentCode && enrollMode === MFA_METHOD_TOTP && (
        <TotpEnrollScreen
          qrCodeDataUrl={qrCodeDataUrl}
          totpSecret={totpSecret}
          onSwitchToSms={() => setEnrollMode(MFA_METHOD_SMS)}
          onQrScanned={handleQrScanned}
          isMfaForcedByOrgAdmin={isMfaForcedByOrgAdmin}
        />
      )}
      {sentCode && (
        <ValidationScreen
          displayUserPhone={displayUserPhone}
          setSentCode={setSentCode}
          firstName={firstName}
          isTwoFactorRequired={isTwoFactorRequired}
          handleSendCodeClick={handleSendCodeClick}
          mfaMethod={mfaMethod}
        />
      )}
    </div>
  );
};

export default AuthenticateUserScreen;
